import { useEffect, useRef, useState } from 'react'
import { X, Trash2, Move, GripVertical, Bell } from 'lucide-react'
import { eventTargetInside } from '@/hooks/useOutsideClick'

const WIDTHS = [
  { value: 1, label: '1px' },
  { value: 2, label: '2px' },
  { value: 3, label: '3px' },
  { value: 4, label: '4px' },
]

const DASHES = [
  { key: 'solid', label: 'Sólida', dash: [] },
  { key: 'dashed', label: 'Discontinua', dash: [6, 4] },
  { key: 'dotted', label: 'Punteada', dash: [2, 3] },
]

const POSITIONS = [
  { key: 'left', label: 'Izq' },
  { key: 'center', label: 'Centro' },
  { key: 'right', label: 'Der' },
]

const COLOR_PRESETS = [
  '#f59e0b', '#ef4444', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899',
  '#06b6d4', '#14b8a6', '#f97316', '#84cc16', '#eab308', '#64748b',
]

const SHAPE_TYPES = new Set(['rect', 'circle'])
const LINE_TYPES = new Set(['trendline', 'hline', 'vline', 'hray', 'vray', 'arrow', 'pencil'])
// Dibujos con un único precio: los que ofrecen «Crear alerta en este precio».
const ALERTABLE_TYPES = new Set(['hline', 'hray'])

function dashKey(dash) {
  if (!dash || dash.length === 0) return 'solid'
  if (dash[0] >= 4) return 'dashed'
  return 'dotted'
}

function timeInputValue(ts) {
  if (!ts) return ''
  const d = new Date(ts * 1000)
  try {
    return d.toISOString().slice(0, 16)
  } catch {
    return ''
  }
}

function typeLabel(type) {
  const map = {
    trendline: 'línea de tendencia',
    hline: 'línea horizontal',
    vline: 'línea vertical',
    hray: 'rayo horizontal',
    vray: 'rayo vertical',
    rect: 'rectángulo',
    circle: 'círculo/elipse',
    pencil: 'lápiz',
    arrow: 'flecha',
    fibonacci: 'retroceso de Fibonacci',
    long: 'posición larga',
    short: 'posición corta',
  }
  return map[type] || type
}

// Dos pieles. La clásica (gris/azul según isDark) es la del chart privado
// (ChartPage), cuyo bundle no carga quantum-theme.css. La Quantum es la del
// chart público, misma familia que PriceAlertForm: panel .quantum-menu,
// campos .quantum-input, oro para lo activo y marfil para el texto.
function editorSkin(quantum, isDark) {
  if (quantum) {
    return {
      panel: 'quantum-menu rounded shadow-xl',
      panelStyle: { color: 'var(--ivory, #F7F5EF)' },
      label: 'block text-[10px] uppercase tracking-wider mb-1',
      labelStyle: { color: 'var(--txt-dim, rgba(247,245,239,0.55))' },
      field: 'quantum-input !py-1 !px-2 !text-sm',
      tinyField: 'quantum-input !py-0.5 !px-1 !text-xs',
      segGroup: 'flex',
      seg: (on) => `quantum-tf-pill flex-1 !px-2 !py-1.5 text-center ${on ? 'on' : ''}`,
      swatch: (on) => (on ? 'border-[color:var(--gold-light)] scale-110' : 'border-transparent hover:scale-105'),
      tabsBar: 'flex gap-1 mb-3 pb-2 border-b border-[color:var(--hair-dim)]',
      tab: (on) => `quantum-chip !px-3 !py-1 ${on ? 'on' : ''}`,
      closeIcon: 'quantum-icon-btn w-7 h-7 rounded flex items-center justify-center',
      alertBtn: 'quantum-header-btn w-full flex items-center justify-center gap-1.5 mb-3 !text-[color:var(--gold-light)] hover:!border-[color:var(--gold-light)]',
      footer: 'flex items-center justify-between gap-2 pt-3 border-t border-[color:var(--hair-dim)]',
      deleteBtn: 'quantum-header-btn flex items-center gap-1 !text-[color:var(--down)] hover:!border-[color:var(--down)]',
      closeBtn: 'quantum-header-btn quantum-header-btn-gold',
      summary: 'mt-3 p-2 rounded border border-[color:var(--hair)] bg-[rgba(247,245,239,0.04)]',
      checkbox: 'quantum-checkbox',
    }
  }
  const idle = isDark ? 'border-gray-600 hover:bg-gray-800' : 'border-slate-200 hover:bg-slate-50'
  const field = `rounded border text-sm ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-slate-50 border-slate-200'}`
  return {
    panel: `rounded-lg border shadow-xl ${isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-slate-200 text-slate-800'}`,
    panelStyle: undefined,
    label: 'block text-xs opacity-70 mb-1',
    labelStyle: undefined,
    field,
    tinyField: `rounded border text-xs ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-slate-50 border-slate-200'}`,
    segGroup: 'flex gap-1',
    seg: (on) => `flex-1 px-2 py-1 text-xs rounded border transition ${on ? 'bg-blue-600 text-white border-blue-600' : idle}`,
    swatch: (on) => (on ? 'border-blue-500 scale-110' : 'border-transparent hover:scale-105'),
    tabsBar: 'flex gap-1 mb-3 border-b border-slate-200 dark:border-gray-700 pb-1',
    tab: (on) => `px-3 py-1 text-xs rounded-t transition ${
      on ? 'bg-blue-600 text-white' : isDark ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-slate-100 text-slate-600'
    }`,
    closeIcon: 'p-1 rounded hover:bg-black/5 dark:hover:bg-white/10',
    alertBtn: 'w-full flex items-center justify-center gap-1.5 mb-2 px-3 py-1.5 text-xs rounded border border-blue-600 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition',
    footer: 'flex items-center justify-between pt-2 border-t border-dashed border-gray-200 dark:border-gray-700',
    deleteBtn: 'flex items-center gap-1 px-3 py-1.5 text-xs rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition',
    closeBtn: 'px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 transition',
    summary: 'mt-3 p-2 rounded border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800',
    checkbox: 'rounded',
  }
}

// onCreateAlert(price) es opcional: solo se pasa donde se pueden crear alertas.
// El atajo copia el precio de la línea; la alerta no queda ligada al dibujo.
// chartAreaRef: el elemento del chart (padre del canvas de dibujo); un click
// real sobre él cierra el panel. quantum: piel del chart público.
export default function DrawingEditor({
  drawing, isDark, onChange, onDelete, onClose, onCreateAlert, chartAreaRef, quantum = false,
}) {
  const panelRef = useRef(null)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragStart = useRef(null)

  // Cierre por click fuera: solo un pointerdown sobre el chart, nunca sobre
  // el propio panel (bug D-10, 2026-10-30). Antes escuchaba mousedown en
  // window con contains(e.target): bajo el shell unificado el árbol vive en
  // un shadow root, e.target llega retargetado al host y TODO click contaba
  // como fuera — el panel se desmontaba antes del click de cualquier opción
  // (mismo fallo que B1, ver useOutsideClick). El resto de la página (cabecera,
  // menús, el diálogo de alerta) no lo cierra: para eso están Cerrar, la X,
  // Eliminar y Escape.
  useEffect(() => {
    const area = chartAreaRef?.current
    if (!area) return
    const handler = (e) => {
      if (eventTargetInside(panelRef.current, e)) return
      onClose()
    }
    area.addEventListener('pointerdown', handler, true)
    return () => area.removeEventListener('pointerdown', handler, true)
  }, [chartAreaRef, onClose])

  // Dragging
  const startDrag = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    dragStart.current = { x: clientX, y: clientY, offsetX: offset.x, offsetY: offset.y }
    document.body.style.userSelect = 'none'
  }

  useEffect(() => {
    const onMove = (e) => {
      if (!dragStart.current) return
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY
      const dx = clientX - dragStart.current.x
      const dy = clientY - dragStart.current.y
      setOffset({ x: dragStart.current.offsetX + dx, y: dragStart.current.offsetY + dy })
    }
    const onEnd = () => {
      dragStart.current = null
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onEnd)
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('touchend', onEnd)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onEnd)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
    }
  }, [offset])

  if (!drawing) return null

  const { type, points } = drawing
  const isShape = SHAPE_TYPES.has(type)
  const isLine = LINE_TYPES.has(type)
  const style = drawing.style || { color: '#f59e0b', lineWidth: 1.5, lineDash: [] }
  const label = drawing.label || { text: '', position: 'center' }
  const p1 = points?.[0] || { time: 0, price: 0 }
  const p2 = points?.[1] || { time: 0, price: 0 }

  const setStyle = (patch) => onChange({ style: { ...style, ...patch } })
  const setLabel = (patch) => onChange({ label: { ...label, ...patch } })
  const setPoint = (idx, patch) => {
    const next = [...points]
    next[idx] = { ...next[idx], ...patch }
    onChange({ points: next })
  }

  const config = drawing.config || { levels: [], showBackground: true, backgroundOpacity: 0.12 }
  const setConfig = (patch) => onChange({ config: { ...config, ...patch } })
  const setLevel = (idx, patch) => {
    const next = [...(config.levels || [])]
    next[idx] = { ...next[idx], ...patch }
    setConfig({ levels: next })
  }

  const hasTabs = type === 'fibonacci' || type === 'long' || type === 'short'
  const [activeTab, setActiveTab] = useState(hasTabs ? 'style' : 'all')
  const panelWidth = type === 'fibonacci' || type === 'long' || type === 'short' ? 'w-[32rem]' : 'w-80'

  const ui = editorSkin(quantum, isDark)
  const inputBase = ui.field
  const tinyInput = ui.tinyField

  const renderStyleSection = () => (
    <>
      {/* Color */}
      <div className="mb-3">
        <label className={ui.label} style={ui.labelStyle}>Color</label>
        <div className="grid grid-cols-6 gap-1.5 mb-2">
          {COLOR_PRESETS.map((c) => (
            <button
              key={c}
              onClick={() => setStyle({ color: c })}
              className={`w-full aspect-square rounded-full border-2 transition ${ui.swatch(style.color?.toLowerCase() === c.toLowerCase())}`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={style.color}
            onChange={(e) => setStyle({ color: e.target.value })}
            className="w-8 h-8 p-0 border-0 rounded cursor-pointer"
          />
          <span className="text-xs font-mono">{style.color}</span>
        </div>
      </div>

      {/* Width */}
      <div className="mb-3">
        <label className={ui.label} style={ui.labelStyle}>Grosor</label>
        <div className={ui.segGroup}>
          {WIDTHS.map((w) => (
            <button
              key={w.value}
              onClick={() => setStyle({ lineWidth: w.value })}
              className={ui.seg(style.lineWidth === w.value)}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Dash style */}
      <div className="mb-3">
        <label className={ui.label} style={ui.labelStyle}>Estilo de línea</label>
        <div className={ui.segGroup}>
          {DASHES.map((d) => (
            <button
              key={d.key}
              onClick={() => setStyle({ lineDash: d.dash })}
              className={ui.seg(dashKey(style.lineDash) === d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {/* Fill (shapes only) */}
      {isShape && (
        <div className="mb-3">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={style.fill !== false}
              onChange={(e) => setStyle({ fill: e.target.checked })}
              className={ui.checkbox}
            />
            <span className="opacity-70">Rellenar figura</span>
          </label>
        </div>
      )}

      {/* Fibonacci background */}
      {type === 'fibonacci' && (
        <div className="mb-3">
          <label className={ui.label} style={ui.labelStyle}>Fondo del retroceso</label>
          <div className="flex items-center gap-3 mb-2">
            <input
              type="range"
              min="0.02"
              max="0.6"
              step="0.02"
              value={config.backgroundOpacity ?? 0.12}
              onChange={(e) => setConfig({ backgroundOpacity: parseFloat(e.target.value) })}
              className="flex-1"
            />
            <span className="text-xs w-10 text-right">{Math.round((config.backgroundOpacity ?? 0.12) * 100)}%</span>
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={config.showBackground !== false}
              onChange={(e) => setConfig({ showBackground: e.target.checked })}
              className={ui.checkbox}
            />
            <span className="opacity-70">Mostrar fondo</span>
          </label>
        </div>
      )}

      {/* Text label */}
      <div className="mb-3">
        <label className={ui.label} style={ui.labelStyle}>Texto</label>
        <input
          type="text"
          value={label.text}
          onChange={(e) => setLabel({ text: e.target.value })}
          placeholder="Ej: soporte"
          className={`w-full px-2 py-1 ${inputBase}`}
        />
        <div className={`${ui.segGroup} mt-1`}>
          {POSITIONS.map((pos) => (
            <button
              key={pos.key}
              onClick={() => setLabel({ position: pos.key })}
              className={ui.seg(label.position === pos.key)}
            >
              {pos.label}
            </button>
          ))}
        </div>
      </div>
    </>
  )

  const renderLevelsSection = () => (
    <div className="mb-3">
      <label className={ui.label} style={ui.labelStyle}>Tramos (24 niveles)</label>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-80 overflow-y-auto pr-1">
        {(config.levels || []).map((lvl, idx) => (
          <div key={idx} className="flex items-center gap-1 text-xs">
            <input
              type="number"
              step="any"
              value={lvl.r ?? ''}
              onChange={(e) => setLevel(idx, { r: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              placeholder="%"
              className={`w-14 px-1 py-0.5 ${tinyInput}`}
            />
            <input
              type="text"
              value={lvl.label || ''}
              onChange={(e) => setLevel(idx, { label: e.target.value })}
              placeholder="Etiqueta"
              className={`w-20 px-1 py-0.5 ${tinyInput}`}
            />
            <input
              type="color"
              value={lvl.color || '#94a3b8'}
              onChange={(e) => setLevel(idx, { color: e.target.value })}
              className="w-6 h-6 p-0 border-0 rounded cursor-pointer"
            />
            <label className="flex items-center gap-0.5 text-[10px] cursor-pointer" title="Mostrar precio">
              <input
                type="checkbox"
                checked={!!lvl.showPrice}
                onChange={(e) => setLevel(idx, { showPrice: e.target.checked })}
                className={ui.checkbox}
              />
              <span>$</span>
            </label>
          </div>
        ))}
      </div>
    </div>
  )

  const renderEntrySection = () => {
    const isLong = type === 'long'
    const entry = points?.[0]?.price ?? 0
    const tp = points?.[1]?.price ?? 0
    const sl = config.slPrice ?? (isLong ? entry - Math.abs(tp - entry) : entry + Math.abs(tp - entry))
    const reward = Math.abs(tp - entry)
    const risk = Math.abs(sl - entry)
    const tickSize = Number(config.tickSize) || Math.max(0.01, Math.pow(10, -Math.floor(Math.log10(entry || 1)) - 1))
    const tpTicks = reward > 0 ? Math.round(reward / tickSize) : 0
    const slTicks = risk > 0 ? Math.round(risk / tickSize) : 0
    const riskAmount = (config.accountSize || 0) * (config.risk || 0) / 100
    const qtyByRisk = risk > 0 ? riskAmount / risk : 0
    const margin = entry > 0 && config.leverage > 0 ? (qtyByRisk * entry) / config.leverage : 0

    const row = (label, children) => (
      <div className="grid grid-cols-[10rem_1fr] items-center gap-2 mb-2">
        <span className="text-xs opacity-70">{label}</span>
        <div className="flex gap-2">{children}</div>
      </div>
    )

    return (
      <div className="mb-3 text-xs">
        {row('Tamaño de la cuenta',
          <>
            <input
              type="number"
              step="any"
              value={config.accountSize ?? ''}
              onChange={(e) => setConfig({ accountSize: parseFloat(e.target.value) })}
              className={`w-28 px-2 py-1 ${inputBase}`}
            />
            <select
              value={config.accountCurrency || 'USDT'}
              onChange={(e) => setConfig({ accountCurrency: e.target.value })}
              className={`px-2 py-1 ${inputBase}`}
            >
              <option value="USDT">USDT</option>
              <option value="USD">USD</option>
              <option value="BTC">BTC</option>
              <option value="ETH">ETH</option>
            </select>
          </>
        )}

        {row('Tamaño del lote',
          <input
            type="number"
            step="any"
            value={config.lotSize ?? ''}
            onChange={(e) => setConfig({ lotSize: parseFloat(e.target.value) })}
            placeholder="Manual"
            className={`w-full px-2 py-1 ${inputBase}`}
          />
        )}

        {row('Riesgo',
          <>
            <input
              type="number"
              step="any"
              value={config.risk ?? ''}
              onChange={(e) => setConfig({ risk: parseFloat(e.target.value) })}
              className={`w-20 px-2 py-1 ${inputBase}`}
            />
            <span className="opacity-70">%</span>
            <span className="opacity-70 ml-auto">≈ {riskAmount.toFixed(2)} {config.accountCurrency || 'USDT'}</span>
          </>
        )}

        {row('Precio de entrada',
          <input
            type="number"
            step="any"
            value={entry}
            onChange={(e) => setPoint(0, { price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
            className={`w-full px-2 py-1 ${inputBase}`}
          />
        )}

        {row('Apalancamiento',
          <input
            type="number"
            step="any"
            value={config.leverage ?? ''}
            onChange={(e) => setConfig({ leverage: parseFloat(e.target.value) })}
            className={`w-full px-2 py-1 ${inputBase}`}
          />
        )}

        {row('Tick size',
          <input
            type="number"
            step="any"
            value={config.tickSize ?? ''}
            onChange={(e) => setConfig({ tickSize: e.target.value === '' ? '' : parseFloat(e.target.value) })}
            placeholder={`auto ${tickSize}`}
            className={`w-full px-2 py-1 ${inputBase}`}
          />
        )}

        <div className="text-[10px] uppercase tracking-wide opacity-50 mt-3 mb-1">Nivel de beneficio</div>
        {row('Ticks',
          <input type="number" value={tpTicks} readOnly className={`w-24 px-2 py-1 opacity-70 ${inputBase}`} />
        )}
        {row('Precio TP',
          <input
            type="number"
            step="any"
            value={tp}
            onChange={(e) => setPoint(1, { price: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
            className={`w-full px-2 py-1 ${inputBase}`}
          />
        )}

        <div className="text-[10px] uppercase tracking-wide opacity-50 mt-3 mb-1">Nivel de stop</div>
        {row('Ticks',
          <input type="number" value={slTicks} readOnly className={`w-24 px-2 py-1 opacity-70 ${inputBase}`} />
        )}
        {row('Precio SL',
          <input
            type="number"
            step="any"
            value={sl}
            onChange={(e) => setConfig({ slPrice: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
            className={`w-full px-2 py-1 ${inputBase}`}
          />
        )}

        <div className={ui.summary}>
          <div className="flex justify-between text-xs mb-1">
            <span className="opacity-70">Cantidad sugerida por riesgo</span>
            <span className="font-mono">{qtyByRisk > 0 ? qtyByRisk.toFixed(6) : '—'}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="opacity-70">Margen requerido ({config.leverage || 1}x)</span>
            <span className="font-mono">{margin > 0 ? margin.toFixed(2) : '—'} {config.accountCurrency || 'USDT'}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="absolute inset-0 z-30 pointer-events-none">
      <div
        ref={panelRef}
        className={`pointer-events-auto absolute left-1/2 top-16 -translate-x-1/2 ${panelWidth} p-4 ${ui.panel}`}
        style={{ ...ui.panelStyle, transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)` }}
        data-testid="drawing-editor"
      >
        {/* Draggable header */}
        <div
          className="flex items-center justify-between mb-3 cursor-move"
          onMouseDown={startDrag}
          onTouchStart={startDrag}
        >
          <h3 className="text-sm font-semibold flex items-center gap-2 select-none">
            <GripVertical size={14} className="opacity-50" />
            <Move size={14} /> Configurar {typeLabel(type)}
          </h3>
          <button onClick={onClose} title="Cerrar" className={ui.closeIcon}>
            <X size={14} />
          </button>
        </div>

        {/* Tabs */}
        {hasTabs && (
          <div className={ui.tabsBar}>
            {[
              { key: 'style', label: 'Estilo' },
              ...(type === 'fibonacci' ? [{ key: 'levels', label: 'Tramos' }] : []),
              ...(type === 'long' || type === 'short' ? [{ key: 'entry', label: 'Entrada' }] : []),
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={ui.tab(activeTab === t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {hasTabs ? (
          activeTab === 'style'
            ? renderStyleSection()
            : activeTab === 'levels'
            ? renderLevelsSection()
            : renderEntrySection()
        ) : (
          <>
            {renderStyleSection()}
            {/* Coordinates (trendline only for now) */}
            {type === 'trendline' && (
              <div className="mb-3">
                <label className={ui.label} style={ui.labelStyle}>Coordenadas</label>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="opacity-60 block mb-0.5">Inicio precio</span>
                    <input
                      type="number"
                      step="any"
                      value={p1.price}
                      onChange={(e) => setPoint(0, { price: parseFloat(e.target.value) })}
                      className={`w-full px-1.5 py-1 ${tinyInput}`}
                    />
                  </div>
                  <div>
                    <span className="opacity-60 block mb-0.5">Fin precio</span>
                    <input
                      type="number"
                      step="any"
                      value={p2.price}
                      onChange={(e) => setPoint(1, { price: parseFloat(e.target.value) })}
                      className={`w-full px-1.5 py-1 ${tinyInput}`}
                    />
                  </div>
                  <div>
                    <span className="opacity-60 block mb-0.5">Inicio tiempo</span>
                    <input
                      type="datetime-local"
                      value={timeInputValue(p1.time)}
                      onChange={(e) => {
                        const ts = Math.floor(new Date(e.target.value).getTime() / 1000)
                        if (!isNaN(ts)) setPoint(0, { time: ts })
                      }}
                      className={`w-full px-1 py-1 ${tinyInput}`}
                    />
                  </div>
                  <div>
                    <span className="opacity-60 block mb-0.5">Fin tiempo</span>
                    <input
                      type="datetime-local"
                      value={timeInputValue(p2.time)}
                      onChange={(e) => {
                        const ts = Math.floor(new Date(e.target.value).getTime() / 1000)
                        if (!isNaN(ts)) setPoint(1, { time: ts })
                      }}
                      className={`w-full px-1 py-1 ${tinyInput}`}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Atajo D-10: copia el precio de la línea al diálogo de crear alerta */}
        {onCreateAlert && ALERTABLE_TYPES.has(type) && Number.isFinite(p1.price) && (
          <button
            onClick={() => onCreateAlert(p1.price)}
            className={ui.alertBtn}
            data-testid="drawing-create-alert"
          >
            <Bell size={12} /> Crear alerta en este precio
          </button>
        )}

        {/* Actions */}
        <div className={ui.footer}>
          <button
            onClick={onDelete}
            className={ui.deleteBtn}
          >
            <Trash2 size={12} /> Eliminar
          </button>
          <button
            onClick={onClose}
            className={ui.closeBtn}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}
